"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/feedback";
import { Label, Input, Select } from "@/components/ui/form-elements";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/overlays";
import {
  Search,
  UserPlus,
  Users,
  Bot,
  Mail,
  Briefcase,
  TrendingUp,
  Plus,
  Sparkles,
  AlertTriangle,
  ArrowRight,
  ShieldCheck,
  Pencil,
  Trash2,
  Network,
} from "lucide-react";
import {
  fetchPeopleAction,
  createAgentAction,
  updatePersonAction,
  deletePersonAction,
} from "@/actions/people-actions";

export default function PeopleDirectoryPage() {
  const [people, setPeople] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit state
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState<"HUMAN" | "AI">("HUMAN");
  const [editRole, setEditRole] = useState<
    "owner" | "admin" | "member" | "viewer"
  >("member");
  const [editTeam, setEditTeam] = useState("Delivery Team");
  const [editEmail, setEditEmail] = useState("");
  const [editSpecialty, setEditSpecialty] = useState("");
  const [editCapacity, setEditCapacity] = useState("80");

  // Search & Filters state
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");

  // Form state
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState<"HUMAN" | "AI">("HUMAN");
  const [formRole, setFormRole] = useState<
    "owner" | "admin" | "member" | "viewer"
  >("member");
  const [formTeam, setFormTeam] = useState("Delivery Team");
  const [formEmail, setFormEmail] = useState("");
  const [formSpecialty, setFormSpecialty] = useState("");
  const [formCapacity, setFormCapacity] = useState("80");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Load people list
  const loadPeople = async () => {
    setLoading(true);
    try {
      const data = await fetchPeopleAction();
      setPeople(data);
    } catch (err) {
      console.error("Error loading people:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPeople();
  }, []);

  // Filter logic
  const filteredPeople = people.filter((person) => {
    const matchesSearch =
      person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      person.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      person.specialty.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRole = roleFilter === "all" || person.role === roleFilter;
    const matchesType = typeFilter === "all" || person.type === typeFilter;
    const matchesTeam = teamFilter === "all" || person.team === teamFilter;

    return matchesSearch && matchesRole && matchesType && matchesTeam;
  });

  // Get unique teams for filters
  const uniqueTeams = Array.from(
    new Set(people.map((p) => p.team).filter(Boolean)),
  );

  const handleCreateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName || !formEmail) return;

    setActionLoading(true);
    try {
      await createAgentAction({
        name: formName,
        type: formType,
        role: formRole,
        team: formTeam,
        email: formEmail,
        specialty:
          formSpecialty ||
          (formType === "AI" ? "AI automation task" : "General developer"),
        capacity: parseInt(formCapacity, 10) || 80,
      });

      // Reset form
      setFormName("");
      setFormEmail("");
      setFormSpecialty("");
      setFormCapacity("80");
      setDialogOpen(false);

      // Reload list
      await loadPeople();
    } catch (err) {
      console.error("Error creating agent:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenEdit = (person: any) => {
    setSelectedPerson(person);
    setEditName(person.name);
    setEditType(person.type);
    setEditRole(person.role);
    setEditTeam(person.team || "Unassigned");
    setEditEmail(person.email);
    setEditSpecialty(person.specialty || "");
    setEditCapacity(person.capacity?.toString() || "80");
    setIsEditOpen(true);
  };

  const handleUpdatePerson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPerson || !editName || !editEmail) return;

    setActionLoading(true);
    try {
      await updatePersonAction({
        id: selectedPerson.id,
        name: editName,
        role: editRole,
        team: editTeam,
        email: editEmail,
        specialty: editSpecialty,
        capacity: parseInt(editCapacity, 10) || 80,
      });

      setIsEditOpen(false);
      setSelectedPerson(null);
      await loadPeople();
    } catch (err) {
      console.error("Error updating person:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePerson = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm("Sei sicuro di voler eliminare questo collaboratore?")) return;

    setActionLoading(true);
    try {
      await deletePersonAction(id);
      await loadPeople();
    } catch (err) {
      console.error("Error deleting person:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Get total stats
  const humanCount = people.filter((p) => p.type === "HUMAN").length;
  const aiCount = people.filter((p) => p.type === "AI").length;
  const criticalCount = people.filter(
    (p) => p.capacity > 90 || p.status === "burnout-risk",
  ).length;

  return (
    <div className="h-full overflow-y-auto px-8 py-8 scrollbar-thin">
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-primary flex items-center gap-2">
              <Users className="h-6 w-6 text-primary-600" />
              People & Agents Directory
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              Gestisci l'anagrafica aziendale, definisci i ruoli di accesso sul
              grafo e monitora i tuoi agenti AI.
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <UserPlus className="h-4 w-4" />
                Aggiungi Collaboratore
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Crea Nuovo Profilo</DialogTitle>
                <DialogDescription>
                  Inserisci i dati per creare una nuova persona o registrare un
                  agente AI autonomo nel grafo.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleCreateAgent} className="space-y-4 my-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5 col-span-2">
                    <Label htmlFor="agent-name">
                      Nome Completo / Identificativo
                    </Label>
                    <Input
                      id="agent-name"
                      placeholder="es. Alessio Corrado o Cleos Archiver"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="agent-type">Tipo Entità</Label>
                    <Select
                      id="agent-type"
                      value={formType}
                      onChange={(e) => {
                        const val = e.target.value as "HUMAN" | "AI";
                        setFormType(val);
                        if (val === "AI") {
                          setFormEmail(
                            `${formName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}@ai.cleos`,
                          );
                        }
                      }}
                    >
                      <option value="HUMAN">Human (Collaboratore)</option>
                      <option value="AI">AI Agent (Autonomo)</option>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="agent-role">Ruolo Permessi</Label>
                    <Select
                      id="agent-role"
                      value={formRole}
                      onChange={(e) => setFormRole(e.target.value as any)}
                    >
                      <option value="owner">Owner (Grafo Completo)</option>
                      <option value="admin">Admin (Modifiche)</option>
                      <option value="member">Member (Scrittura)</option>
                      <option value="viewer">Viewer (Sola Lettura)</option>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="agent-team">Team Principale</Label>
                    <Select
                      id="agent-team"
                      value={formTeam}
                      onChange={(e) => setFormTeam(e.target.value)}
                    >
                      <option value="Delivery Team">Delivery Team</option>
                      <option value="Product & Design">Product & Design</option>
                      <option value="Core Tech">Core Tech</option>
                      <option value="Growth & Sales">Growth & Sales</option>
                      <option value="Finance">Finance</option>
                      <option value="Unassigned">
                        Nessun Team (Unassigned)
                      </option>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="agent-capacity">
                      Capacità iniziale (%)
                    </Label>
                    <Input
                      id="agent-capacity"
                      type="number"
                      min="0"
                      max="100"
                      value={formCapacity}
                      onChange={(e) => setFormCapacity(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="agent-email">Indirizzo Email</Label>
                  <Input
                    id="agent-email"
                    type="email"
                    placeholder="es. alessio@quario.ai"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="agent-specialty">
                    Competenza Principale / Specialità
                  </Label>
                  <Input
                    id="agent-specialty"
                    placeholder="es. UX Design, Optimization, Ingestion RAG"
                    value={formSpecialty}
                    onChange={(e) => setFormSpecialty(e.target.value)}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <DialogClose asChild>
                    <Button variant="secondary" type="button">
                      Annulla
                    </Button>
                  </DialogClose>
                  <Button type="submit" loading={actionLoading}>
                    Registra nel Grafo
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Modifica Profilo</DialogTitle>
                <DialogDescription>
                  Modifica i dati del collaboratore o dell'agente AI nel grafo.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleUpdatePerson} className="space-y-4 my-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5 col-span-2">
                    <Label htmlFor="edit-agent-name">
                      Nome Completo / Identificativo
                    </Label>
                    <Input
                      id="edit-agent-name"
                      placeholder="es. Alessio Corrado o Cleos Archiver"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-agent-type">Tipo Entità</Label>
                    <Select
                      id="edit-agent-type"
                      value={editType}
                      disabled
                      onChange={(e) => setEditType(e.target.value as any)}
                    >
                      <option value="HUMAN">Human (Collaboratore)</option>
                      <option value="AI">AI Agent (Autonomo)</option>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="edit-agent-role">Ruolo Permessi</Label>
                    <Select
                      id="edit-agent-role"
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value as any)}
                    >
                      <option value="owner">Owner (Grafo Completo)</option>
                      <option value="admin">Admin (Modifiche)</option>
                      <option value="member">Member (Scrittura)</option>
                      <option value="viewer">Viewer (Sola Lettura)</option>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-agent-team">Team Principale</Label>
                    <Select
                      id="edit-agent-team"
                      value={editTeam}
                      onChange={(e) => setEditTeam(e.target.value)}
                    >
                      <option value="Delivery Team">Delivery Team</option>
                      <option value="Product & Design">Product & Design</option>
                      <option value="Core Tech">Core Tech</option>
                      <option value="Growth & Sales">Growth & Sales</option>
                      <option value="Finance">Finance</option>
                      <option value="Unassigned">
                        Nessun Team (Unassigned)
                      </option>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="edit-agent-capacity">Capacità (%)</Label>
                    <Input
                      id="edit-agent-capacity"
                      type="number"
                      min="0"
                      max="100"
                      value={editCapacity}
                      onChange={(e) => setEditCapacity(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="edit-agent-email">Indirizzo Email</Label>
                  <Input
                    id="edit-agent-email"
                    type="email"
                    placeholder="es. alessio@quario.ai"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="edit-agent-specialty">
                    Competenza Principale / Specialità
                  </Label>
                  <Input
                    id="edit-agent-specialty"
                    placeholder="es. UX Design, Optimization, Ingestion RAG"
                    value={editSpecialty}
                    onChange={(e) => setEditSpecialty(e.target.value)}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <DialogClose asChild>
                    <Button variant="secondary" type="button">
                      Annulla
                    </Button>
                  </DialogClose>
                  <Button type="submit" loading={actionLoading}>
                    Salva Modifiche
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Quick KPI Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="flex flex-col justify-between py-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Collaboratori Umani
              </span>
              <Users className="h-4 w-4 text-text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-text-primary">
                {loading ? "..." : humanCount}
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col justify-between py-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Agenti AI Attivi
              </span>
              <Bot className="h-4 w-4 text-text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-text-primary">
                {loading ? "..." : aiCount}
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col justify-between py-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Carichi Critici (Burnout)
              </span>
              <AlertTriangle className="h-4 w-4 text-error" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-error flex items-baseline gap-2">
                {loading ? "..." : criticalCount}
                {criticalCount > 0 && (
                  <span className="text-xs font-medium text-text-secondary">
                    richiede attenzione
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Directory Search and Filter Panel */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Cerca per nome, email o competenza..."
                className="pl-9 h-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filter Dropdowns */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-secondary whitespace-nowrap">
                  Tipo:
                </span>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-32 h-9 py-0"
                >
                  <option value="all">Tutti</option>
                  <option value="HUMAN">Solo Umani</option>
                  <option value="AI">Solo AI Agent</option>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-secondary whitespace-nowrap">
                  Ruolo:
                </span>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-36 h-9 py-0"
                >
                  <option value="all">Tutti i ruoli</option>
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-secondary whitespace-nowrap">
                  Team:
                </span>
                <Select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  className="w-40 h-9 py-0"
                >
                  <option value="all">Tutti i team</option>
                  {uniqueTeams.map((teamName) => (
                    <option key={teamName} value={teamName}>
                      {teamName}
                    </option>
                  ))}
                  <option value="Unassigned">Senza Team</option>
                </Select>
              </div>
            </div>
          </div>
        </Card>

        {/* Directory Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-border-strong/20" />
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-border-strong/20 rounded w-2/3" />
                      <div className="h-3 bg-border-strong/20 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-border/50 pt-4">
                    <div className="h-3 bg-border-strong/20 rounded w-3/4" />
                    <div className="h-3 bg-border-strong/20 rounded w-5/6" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : filteredPeople.length === 0 ? (
          <Card className="flex flex-col items-center justify-center p-12 text-center bg-card/50 border-dashed">
            <Users className="h-12 w-12 text-text-muted mb-4" />
            <h3 className="font-semibold text-text-primary text-base">
              Nessun collaboratore trovato
            </h3>
            <p className="text-sm text-text-secondary max-w-sm mt-1">
              La ricerca non ha prodotto risultati. Riprova modificando la query
              o i filtri selezionati.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSearchQuery("");
                setRoleFilter("all");
                setTypeFilter("all");
                setTeamFilter("all");
              }}
            >
              Azzera Filtri
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPeople.map((person) => {
              const isAI = person.type === "AI";
              const isBurnout =
                person.capacity > 90 || person.status === "burnout-risk";

              return (
                <Card
                  key={person.id}
                  onClick={() => handleOpenEdit(person)}
                  className="relative flex flex-col justify-between group h-full cursor-pointer hover:shadow-md transition-shadow select-none"
                >
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div
                          className={`h-12 w-12 rounded-full flex items-center justify-center font-bold text-base shadow-sm select-none shrink-0 ${
                            isAI
                              ? "bg-category-innovation-soft border border-category-innovation-border text-category-innovation"
                              : "bg-primary-600 text-text-on-primary"
                          }`}
                        >
                          {person.avatar}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-text-primary text-base truncate hover:text-primary-600 transition-colors">
                            <Link
                              href={`/people/${person.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {person.name}
                            </Link>
                          </h3>
                          <p className="text-xs text-text-secondary truncate mt-0.5">
                            {person.team}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div
                          className="absolute top-4 right-4 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all pointer-events-none group-hover:pointer-events-auto bg-card border border-border shadow-xs px-1.5 py-1 rounded-lg z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link
                            href={`/explore?nodeId=${person.id}`}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                            title="Vedi nel Grafo"
                          >
                            <Network className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            onClick={() => handleOpenEdit(person)}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                            title="Modifica"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleDeletePerson(e, person.id)}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-error transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <Badge
                          variant={isAI ? "innovation" : "people"}
                          className="group-hover:opacity-0 transition-opacity"
                        >
                          {isAI ? "AI Agent" : "Human"}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 py-0 flex-1 space-y-4">
                    {/* Role & Mail details */}
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Mail className="h-3.5 w-3.5" />
                        <span className="truncate">{person.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Briefcase className="h-3.5 w-3.5" />
                        <span className="truncate">{person.specialty}</span>
                      </div>
                    </div>

                    {/* Capacity indicators */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-text-secondary font-medium">
                          Carico di Lavoro (Capacity):
                        </span>
                        <span
                          className={`font-semibold ${
                            isBurnout
                              ? "text-error"
                              : person.capacity > 75
                                ? "text-warning"
                                : "text-success"
                          }`}
                        >
                          {person.capacity}%
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-border-strong/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            isBurnout
                              ? "bg-error"
                              : person.capacity > 75
                                ? "bg-warning"
                                : "bg-success"
                          }`}
                          style={{ width: `${person.capacity}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="p-6 pt-4 flex justify-between items-center border-t border-border/40 mt-4 bg-card-muted/50 rounded-b-xl">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-text-secondary">
                      <ShieldCheck className="h-3.5 w-3.5 text-primary-500" />
                      Role: {person.role}
                    </span>
                    <Link
                      href={`/people/${person.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 px-2.5 h-8 text-xs hover:bg-card"
                      >
                        Dettaglio Profilo
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
